/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useRef } from 'react';
import { createContext, useEffect, useMemo, useState, useCallback } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useRelayEnvironment } from 'react-relay';

import { useFlags } from '@atlaskit/flag';
import { Box } from '@atlaskit/primitives/compiled';

import { setMicrositeUser } from '../../common/analytics/analytics-client';
import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { setSentryUser } from '../../common/sentry';
import { LoadingView } from '../../ui/components/LoadingView';

import {
	attemptClientLogin,
	cleanupClientAuthFlow,
	consumeLoginPendingMarker,
	hasAutomaticAuthFailed,
} from './auth-flow';
import { type LoggedInUser, type UserContextValue } from './types';
import { queryUserProfile } from './utils';

const styles = cssMap({
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100vw',
		height: '100vh',
	},
});

export const UserContext: React.Context<UserContextValue> =
	createContext<UserContextValue>(undefined);

type UserProviderProps = {
	children: React.ReactNode;
	onUserLoginComplete?: (user: LoggedInUser) => Promise<void>;
};

/**
 * UserProvider manages the authentication state and user profile data.
 *
 * Authentication Flow:
 * 1. On mount, attempts to fetch user profile data
 * 2. If profile data is incomplete or fetch fails, triggers handleAuthFailure
 * 3. handleAuthFailure will:
 *    - If automatic auth fails, show a flag to user to manually clear cookies
 *
 * The heartbeat component also triggers handleAuthFailure if session becomes invalid.
 */
export function UserProvider({
	children,
	onUserLoginComplete,
}: UserProviderProps): React.JSX.Element {
	const [identity, setIdentity] = useState<LoggedInUser>({
		id: '',
		picture: '',
		accountId: '',
		name: '',
		nickname: '',
		email: '',
		staffId: '',
		isAtlassianEmployee: false,
		organisation: '',
		jobTitle: '',
		orgId: '',
		createdAt: '',
	});
	const loginRedirectFailed = useRef<boolean>(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const environment = useRelayEnvironment();
	const { showFlag } = useFlags();
	const { fireEvent } = useAnalyticsEvents();

	const handleAuthFailure = useCallback(async () => {
		if (hasAutomaticAuthFailed()) {
			loginRedirectFailed.current = true;
			return;
		}
	}, []);

	const fetchUserProfile = useCallback(async () => {
		const res = await queryUserProfile(environment);

		if (res.success) {
			const { identity } = res;

			if (identity.accountId) {
				// Execute post login if provided
				if (onUserLoginComplete) {
					await onUserLoginComplete?.(identity);
				}

				setIdentity(identity);
				setMicrositeUser(identity.accountId, identity.orgId, null);
				setSentryUser(identity.accountId);

				if (consumeLoginPendingMarker()) {
					fireEvent('track.user.loggedIn', {});
				}
			} else {
				setIdentity(identity);
			}
		} else {
			setError(true);
			await handleAuthFailure();
		}
	}, [environment, handleAuthFailure, onUserLoginComplete, fireEvent]);

	useEffect(() => {
		fetchUserProfile().finally(() => setLoading(false));
	}, [fetchUserProfile]);

	useEffect(() => {
		if (!loginRedirectFailed.current) {
			return;
		}

		// For whatever reason, attempting to redirect to login has failed and we need
		// to provide a way for the user to manually resolve their issue.
		showFlag({
			id: 'redirection-failed',
			title: 'Automatic login has failed',
			description: 'Please clear your cookies and try again, or use incognito mode.',
		});

		cleanupClientAuthFlow();
	}, [loginRedirectFailed, showFlag]);

	// `isAuthed` is the single boolean source of truth for "the user is
	// signed in and we know who they are". Pre-computed here so consumers
	// (analytics hooks, route gating in `home/index.tsx`, etc.) don't have
	// to re-derive it on every render.
	const isAuthed = !loading && !error && identity.accountId.length > 0;

	const value = useMemo(
		() => ({
			identity,
			loading,
			error,
			isAuthed,
			attemptUserLogin: attemptClientLogin,
			handleAuthFailure,
		}),
		[identity, loading, error, isAuthed, handleAuthFailure],
	);

	return (
		<UserContext.Provider value={value}>
			{loading ? (
				// Block descendants from mounting until `fetchUserProfile` (and the
				// awaited `onUserLoginComplete`, which bootstraps Statsig feature
				// gates) has resolved. Without this gate, providers below — most
				// notably <LocaleProvider> — would evaluate `fg(...)` before the
				// gate client is identified and permanently cache the default
				// (false) value for the rest of the session.
				<Box xcss={styles.loadingContainer} testId="twg-microsite.user-provider.loading">
					<LoadingView appearance="spinner" holdName="twg-microsite.user-provider.loading" />
				</Box>
			) : (
				children
			)}
		</UserContext.Provider>
	);
}
